import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementBlock as _createElementBlock, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "payment-select" }
const _hoisted_2 = {
  key: 1,
  class: "payment-select__block"
}
const _hoisted_3 = { class: "payment-select__label" }
const _hoisted_4 = {
  key: 2,
  class: "payment-select__block"
}
const _hoisted_5 = {
  key: 0,
  class: "payment-select__block"
}

import { computed, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import getBalance from '@exchange/helpers/balance';
import BigNumber from '@exchange/helpers/bignumber';
import { accountService } from '@exchange/libs/account/service/src';
import { balanceService, BalanceModel } from '@exchange/libs/balances/service/src';
import { marginService } from '@exchange/libs/futures/service/src';
import { CURRENCY_ELIGIBLE_FOR_MARGIN } from '@exchange/libs/futures/service/src/lib/constants.config';
import type { ModalVariant } from '@exchange/libs/modals/src';
import { SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import type { FundingProviderModel } from '@exchange/libs/transactions/funds/service/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';

import PaymentSelectOption from './PaymentSelectOption.vue';
import PaymentSingle, { type PaymentSingleItem } from './PaymentSingle.vue';

export type Action = 'deposit' | 'withdraw';


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentSelect',
  props: {
  action: { type: String as PropType<Action>, required: true },
  currency: { type: Object as PropType<CurrencyModel>, required: true },
  currencyNetwork: { type: String, required: false },
  currentPayment: { type: Object as PropType<FundingProviderModel>, required: false },
  disabled: { type: Boolean, default: false },
  labelText: { type: String, required: true },
  payments: { type: Array as PropType<Array<FundingProviderModel>>, required: true },
  placeholderText: { type: String, required: true },
  variant: { type: String as PropType<ModalVariant>, required: true },
},
  emits: ["update:currentPayment"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n({ useScope: 'global' });

const { mainAccountType } = accountService;
const { balances } = balanceService;
const { balance: marginBalance } = marginService;

const firstSelectDone = ref(false);

const getBlockchainFee = (method: FundingProviderModel) => (props.action === 'deposit' ? '0' : new BigNumber(method.feeFix).toFixed(props.currency.transactionPrecision));

const getPaymentMethodInfo = (method: FundingProviderModel) => {
  const isBlockchain = method.isBlockchain();
  const { isInstant } = method;
  const blockchainFee = getBlockchainFee(method);

  return {
    isBlockchain,
    isInstant,
    blockchainFee,
  };
};

const getMethodInfo = (method: FundingProviderModel) => {
  const name = t(`modules.funds.api.transferMethods.${method.title}`);
  const network = {
    label: t('modules.transactions.paymentOptions.network.title'),
    infoMessage: t('modules.transactions.paymentOptions.network.infoMessage'),
  };
  const { isBlockchain, isInstant } = getPaymentMethodInfo(method);

  if (isBlockchain) {
    const methodInfo: PaymentSingleItem = {
      label: props.labelText,
      value: name,
    };
    const networkInfo: PaymentSingleItem | undefined = props.currencyNetwork
      ? {
          label: network.label,
          value: props.currencyNetwork,
          info: {
            message: network.infoMessage,
          },
        }
      : undefined;

    return {
      method: methodInfo,
      network: networkInfo,
    };
  }

  const daysToTransfer = t(`modules.transactions.paymentOptions.${isInstant ? 'instant' : 'notInstant'}`);

  const methodInfo = {
    label: props.labelText,
    value: `${name}\n${daysToTransfer}`,
  };

  return {
    method: methodInfo,
    network: undefined,
  };
};

const singlePaymentMethod = computed(() => {
  const [method] = props.payments;

  if (props.payments.length === 1 && method) {
    return getMethodInfo(method);
  }

  return undefined;
});
const currentPaymentMethodInfo = computed(() => (props.currentPayment ? getMethodInfo(props.currentPayment) : undefined));

const balance = computed(() => {
  const currencyCode = props.currency.symbol;
  const correspondingBalance = balances.value[currencyCode];

  if (mainAccountType.value === SubaccountType.FUTURES && currencyCode === CURRENCY_ELIGIBLE_FOR_MARGIN) {
    return new BalanceModel({
      locked: new BigNumber(0),
      available: new BigNumber(marginBalance.value.available),
      currency_code: currencyCode,
      sequence: correspondingBalance?.sequence ?? -1,
    });
  }

  return (
    correspondingBalance ||
    new BalanceModel({
      locked: '0',
      available: '0',
      currency_code: currencyCode,
      sequence: -1,
    })
  );
});

const balanceAvailableWithPrecision = computed(() => getBalance({ v: new BigNumber(balance.value.available), precision: props.currency.transactionPrecision }));

const activate = (payment: FundingProviderModel) => {
  emit('update:currentPayment', payment);
  firstSelectDone.value = true;
};

return (_ctx: any,_cache: any) => {
  const _component_ot_select = _resolveComponent("ot-select")!
  const _component_ot_transition_fade = _resolveComponent("ot-transition-fade")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (singlePaymentMethod.value)
      ? (_openBlock(), _createBlock(PaymentSingle, {
          key: 0,
          class: "payment-select__block",
          method: singlePaymentMethod.value.method,
          network: singlePaymentMethod.value.network
        }, null, 8, ["method", "network"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(__props.labelText), 1),
          _createVNode(_component_ot_select, {
            class: _normalizeClass(["payment-select__input", { 'payment-select__input--placeholder': !__props.currentPayment }]),
            "data-placeholder": __props.placeholderText,
            "menu-top-space": false,
            variant: __props.variant === 'dark' ? 'dark-outlined' : 'light-outlined',
            label: "slotName",
            options: __props.payments,
            disabled: false,
            value: __props.currentPayment,
            "onUpdate:value": activate
          }, {
            "selected-option": _withCtx((selected) => [
              selected
                ? (_openBlock(), _createBlock(PaymentSelectOption, {
                    key: 0,
                    "blockchain-fee": getBlockchainFee(selected),
                    currency: __props.currency,
                    "currency-network": __props.currencyNetwork,
                    "is-blockchain": selected.isBlockchain(),
                    "is-instant": selected.isInstant,
                    "payment-option": selected,
                    variant: __props.variant
                  }, null, 8, ["blockchain-fee", "currency", "currency-network", "is-blockchain", "is-instant", "payment-option", "variant"]))
                : _createCommentVNode("", true)
            ]),
            option: _withCtx((option) => [
              _createVNode(PaymentSelectOption, {
                "blockchain-fee": getBlockchainFee(option),
                currency: __props.currency,
                "currency-network": __props.currencyNetwork,
                "is-blockchain": option.isBlockchain(),
                "is-instant": option.isInstant,
                "is-list-item": true,
                "payment-option": option,
                variant: __props.variant
              }, null, 8, ["blockchain-fee", "currency", "currency-network", "is-blockchain", "is-instant", "payment-option", "variant"])
            ]),
            _: 1
          }, 8, ["class", "data-placeholder", "variant", "options", "value"]),
          (currentPaymentMethodInfo.value)
            ? (_openBlock(), _createBlock(PaymentSingle, {
                key: 0,
                class: "payment-select__current-payment-block",
                method: currentPaymentMethodInfo.value.method,
                network: currentPaymentMethodInfo.value.network
              }, null, 8, ["method", "network"]))
            : _createCommentVNode("", true)
        ])),
    (_ctx.$slots['error'] && !firstSelectDone.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _renderSlot(_ctx.$slots, "error")
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_ot_transition_fade, null, {
      default: _withCtx(() => [
        (_ctx.$slots['info'])
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _renderSlot(_ctx.$slots, "info")
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 3
    }),
    _createVNode(_component_ot_transition_fade, null, {
      default: _withCtx(() => [
        (__props.currentPayment)
          ? _renderSlot(_ctx.$slots, __props.currentPayment.slotName, {
              key: 0,
              paymentContentClass: 'payment-select__content',
              currentPayment: __props.currentPayment,
              balance: balance.value,
              balanceAvailableWithPrecision: balanceAvailableWithPrecision.value
            })
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ]))
}
}

})